// A microcircuit run's spikes by cell type (cells-sim-v0, kind microcircuit): the brief's "a microcircuit
// fires". Each type gets its rows of a raster (a spike at its time, on its cell's row), its mean rate, the
// cells that never fired, and its population rate over the run. Spikes are the run's own somatic
// threshold crossings (`spikes/`), so nothing here needs its voltage series, which a 1,000-cell run
// chunks by time across every compartment.

import type { CellEntry } from './types.ts';
import { duration, type TimeAxis } from './time.ts';

/** A colour a type, by its name: pyramidal cells in the first series colour, each interneuron class its own. */
const TYPE_COLOUR: [RegExp, string][] = [
  [/pyramidal|\bPN\b|\bPYR\b/i, '#7cc4ff'],
  [/\bSST\b|somatostatin|martinotti/i, '#ff9a4d'],
  [/\bPV\b|parvalbumin|basket/i, '#5fe0c8'],
  [/\bVIP\b/i, '#e58cff'],
];
const OTHER = ['#f5cf4a', '#a7e36b', '#c3b5ff', '#ff6b6b'];

/** Population rates in bins this long, s: 25 ms keeps a 50-cell type's rate readable. */
export const RATE_BIN_S = 0.025;

export interface CellGroup {
  /** The run's own type ('L2/3 SST'). */
  type: string;
  /** Without its layer, as the Cells rows say it: 'SST', 'Pyramidal'. */
  label: string;
  colour: string;
  /** Its cells' indices in the run, in run order: row r is cells[r]. */
  cells: number[];
  spikes: number;
  /** Mean over its cells and the run, Hz. */
  hz: number;
  /** Cells that never fired. */
  silent: number;
  /** The raster: each spike's time, s, and its cell's row. */
  times: Float32Array;
  rows: Float32Array;
  /** Its population rate, Hz a cell, in bins of `binS` from the run's start. */
  rate: Float32Array;
}

export interface Circuit {
  groups: CellGroup[];
  /** The run's span, s. */
  t0: number;
  seconds: number;
  binS: number;
}

/** A type's label: its layer dropped and its first letter raised ('L2/3 pyramidal' → 'Pyramidal'). */
export const typeLabel = (type: string): string => type.replace(/^L\d+(?:\/\d+)?\s+/, '').replace(/^./, (c) => c.toUpperCase());

/** A run's cells grouped by type in the order the types first appear, with each type's spikes. */
export function circuitOf(cells: readonly CellEntry[], spikes: { times: Float32Array; cell: Uint32Array } | null, axis: TimeAxis, binS = RATE_BIN_S): Circuit {
  const seconds = duration({ ...axis, periodic: false });
  const t0 = axis.t0;
  const order: string[] = [];
  const byType = new Map<string, number[]>();
  for (const c of cells) {
    const type = c.type ?? 'Cell';
    if (!byType.has(type)) {
      byType.set(type, []);
      order.push(type);
    }
    byType.get(type)!.push(c.index);
  }
  // Each cell's group and row.
  const groupOf = new Map<number, { g: number; row: number }>();
  order.forEach((type, g) => byType.get(type)!.forEach((cell, row) => groupOf.set(cell, { g, row })));
  // A spike counts where it falls in the run, give or take half a frame (times are stored as f32).
  const slack = Math.max(1e-6, seconds / Math.max(1, axis.frames - 1) / 2);
  const inRun = (t: number) => t >= t0 - slack && t <= t0 + seconds + slack;
  const counts = order.map(() => 0);
  const fired = order.map(() => new Set<number>());
  const n = spikes ? Math.min(spikes.times.length, spikes.cell.length) : 0;
  for (let i = 0; i < n; i++) {
    const at = groupOf.get(spikes!.cell[i]!);
    if (!at || !inRun(spikes!.times[i]!)) continue;
    counts[at.g]!++;
    fired[at.g]!.add(spikes!.cell[i]!);
  }
  // Whole bins, and one for the rest of the run (not one for a rounding error's worth).
  const bins = Math.max(1, Math.ceil(seconds / binS - 1e-9));
  let other = 0;
  const groups: CellGroup[] = order.map((type, g) => {
    const colour = TYPE_COLOUR.find(([re]) => re.test(type))?.[1] ?? OTHER[other++ % OTHER.length]!;
    return { type, label: typeLabel(type), colour, cells: byType.get(type)!, spikes: counts[g]!, hz: 0, silent: 0, times: new Float32Array(counts[g]!), rows: new Float32Array(counts[g]!), rate: new Float32Array(bins) };
  });
  const filled = order.map(() => 0);
  for (let i = 0; i < n; i++) {
    const at = groupOf.get(spikes!.cell[i]!);
    const t = spikes!.times[i]!;
    if (!at || !inRun(t)) continue;
    const k = filled[at.g]!++;
    groups[at.g]!.times[k] = t;
    groups[at.g]!.rows[k] = at.row;
    groups[at.g]!.rate[Math.min(bins - 1, Math.max(0, Math.floor((t - t0) / binS)))]!++;
  }
  for (const [g, x] of groups.entries()) {
    const k = x.cells.length;
    x.hz = k && seconds > 0 ? x.spikes / (k * seconds) : 0;
    x.silent = k - fired[g]!.size;
    // The last bin is only as wide as what's left of the run.
    for (let b = 0; b < bins; b++) {
      const w = Math.min(binS, seconds - b * binS);
      x.rate[b] = k && w > 0 ? x.rate[b]! / (k * w) : 0;
    }
  }
  return { groups, t0, seconds, binS };
}

/** What a circuit is read from: a cells run's cells, clock and spikes (CellsSim). */
interface CircuitRun {
  cells: readonly CellEntry[];
  axis: TimeAxis;
  spikes(): Promise<{ times: Float32Array; cell: Uint32Array } | null>;
}

const loaded = new WeakMap<CircuitRun, Promise<Circuit>>();

/** A run's circuit, read once a run: the Cells rows and the raster share it. */
export function loadCircuit(run: CircuitRun): Promise<Circuit> {
  let p = loaded.get(run);
  if (!p) {
    p = run.spikes().then((s) => circuitOf(run.cells, s, run.axis));
    loaded.set(run, p);
    p.catch(() => loaded.delete(run));
  }
  return p;
}
